// ---------------------------------------------------------------
// Rental management — for customers who rent their vehicles out.
// Per-vehicle: enable rental mode + default pricing. Each rental is a record
// (renter, days, price, mileage allowance, start/end odometer, extensions).
// All math (days elapsed, due date, miles used vs allowed, overage charge,
// running total, revenue) is derived here so every view stays consistent.
// Stored locally for now (moves to the backend with the rest of the data).
// ---------------------------------------------------------------

const SET_KEY = 'dd-rental-settings'; // per-device rental config
const REN_KEY = 'dd-rentals';         // rental records

const getJSON = (k) => { try { return JSON.parse(localStorage.getItem(k)) || {}; } catch { return {}; } };
const getArr = (k) => { try { return JSON.parse(localStorage.getItem(k)) || []; } catch { return []; } };
const save = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };

export const DAY_MS = 24 * 60 * 60 * 1000;

// The car's REAL odometer in miles, read from the OBD total-odometer element
// (io389, reported in km) — not Traccar's accumulated trip distance. Falls back
// to totalDistance only if the OBD odometer isn't reported. One source of truth
// so the rental start/end mileage matches the panel's "Odometer".
export function odoMilesFromAttrs(a = {}) {
  if (a.io389 != null) return a.io389 * 0.621371;
  if (a.totalDistance != null) return (a.totalDistance / 1000) * 0.621371;
  return null;
}

// ---- per-vehicle rental settings ----
export function getRentalSettings(deviceId) {
  const all = getJSON(SET_KEY);
  return { enabled: false, dailyPrice: 0, includedMilesPerDay: 150, overageRate: 0.25, ...(all[deviceId] || {}) };
}
export function saveRentalSettings(deviceId, patch) {
  const all = getJSON(SET_KEY);
  all[deviceId] = { ...getRentalSettings(deviceId), ...patch };
  save(SET_KEY, all);
  return all[deviceId];
}
export const isRentalVehicle = (deviceId) => getRentalSettings(deviceId).enabled;
// true if ANY vehicle has rentals turned on (drives whether the Rentals tab shows)
export const anyRentalEnabled = () => Object.values(getJSON(SET_KEY)).some((s) => s && s.enabled);

// ---- rental records ----
// actor = who's viewing/managing: omit (owner) → see ALL rentals for the car;
// a broker actor (e.g. 'b:B123') → only that broker's own rentals. Legacy
// rentals with no actor are treated as the owner's.
export function getRentals(deviceId, actor) {
  let list = getArr(REN_KEY).filter((r) => !deviceId || r.deviceId === deviceId);
  if (actor) list = list.filter((r) => (r.actor || 'owner') === actor);
  return list.sort((a, b) => new Date(b.startAt) - new Date(a.startAt));
}
export const getActiveRental = (deviceId, actor) => getRentals(deviceId, actor).find((r) => r.status === 'active') || null;
export const getDrafts = (deviceId, actor) => getRentals(deviceId, actor).filter((r) => r.status === 'draft');

// A draft is a rental being prepared — all info can be filled and the renter can
// sign, but the 24h clock does NOT run until it's activated. Lets the broker/owner
// pre-fill everything and finish at pickup.
export function createDraft(deviceId, actor) {
  const all = getArr(REN_KEY);
  const s = getRentalSettings(deviceId);
  const rec = {
    id: `R${Date.now()}`, deviceId, actor: actor || 'owner',
    renter: '', phone: '', email: '', renterLicense: '',
    startAt: null, days: 1,
    dailyPrice: Number(s.dailyPrice) || 0, includedMilesPerDay: Number(s.includedMilesPerDay) || 0, overageRate: Number(s.overageRate) || 0,
    startOdoMi: null, endOdoMi: null, extensions: [],
    paid: 0, deposit: 0, depositReturned: null, depositReason: '', chargeReason: '',
    tollRate: 0.5, tolls: [], // $ per toll passed (editable) + detected toll roads
    status: 'draft', closedAt: null, notes: '', createdAt: new Date().toISOString(),
  };
  all.push(rec); save(REN_KEY, all); return rec;
}
// Turn a draft into a live rental — the clock starts now (or at startAt if given).
export function activateRental(id, startAt) {
  const all = getArr(REN_KEY);
  const r = all.find((x) => x.id === id);
  if (r) { r.status = 'active'; r.startAt = startAt ? new Date(startAt).toISOString() : new Date().toISOString(); save(REN_KEY, all); }
  return r;
}

export function startRental(deviceId, { renter, phone, email, days, dailyPrice, includedMilesPerDay, overageRate, startOdoMi, notes, startAt, paid, deposit }) {
  const all = getArr(REN_KEY);
  const rec = {
    id: `R${Date.now()}`, deviceId,
    renter: renter || '', phone: phone || '', email: email || '',
    // custom start date for a backdated rental, else now
    startAt: startAt ? new Date(startAt).toISOString() : new Date().toISOString(),
    days: Number(days) || 1,
    dailyPrice: Number(dailyPrice) || 0,
    includedMilesPerDay: Number(includedMilesPerDay) || 0,
    overageRate: Number(overageRate) || 0,
    startOdoMi: startOdoMi != null ? Number(startOdoMi) : null,
    endOdoMi: null,
    extensions: [], // [{ days, dailyPrice, at }]
    // money
    paid: Number(paid) || 0,           // how much the renter has paid so far
    deposit: Number(deposit) || 0,     // security deposit held
    depositReturned: null,             // set at close-out
    depositReason: '',                 // reason if deposit not fully returned
    status: 'active', closedAt: null, notes: notes || '',
  };
  all.push(rec); save(REN_KEY, all); return rec;
}
export function extendRental(id, addDays, newDailyPrice) {
  const all = getArr(REN_KEY);
  const r = all.find((x) => x.id === id);
  if (r) {
    r.extensions.push({ days: Number(addDays) || 0, dailyPrice: newDailyPrice != null ? Number(newDailyPrice) : r.dailyPrice, at: new Date().toISOString() });
    save(REN_KEY, all);
  }
  return r;
}
export function closeRental(id, endOdoMi) {
  const all = getArr(REN_KEY);
  const r = all.find((x) => x.id === id);
  if (r) { r.status = 'closed'; r.closedAt = new Date().toISOString(); if (endOdoMi != null) r.endOdoMi = Number(endOdoMi); save(REN_KEY, all); }
  return r;
}
export function updateRental(id, patch) {
  const all = getArr(REN_KEY);
  const r = all.find((x) => x.id === id);
  if (r) { Object.assign(r, patch); save(REN_KEY, all); }
  return r;
}
export function deleteRental(id) { save(REN_KEY, getArr(REN_KEY).filter((r) => r.id !== id)); }

// ---- Do Not Rent list (global, keyed by driver's license number) ----
const DNR_KEY = 'dd-do-not-rent';
// normalize a license for matching: uppercase, strip spaces/dashes/punctuation
const normLic = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
export function getDoNotRent() { return getArr(DNR_KEY).sort((a, b) => new Date(b.at) - new Date(a.at)); }
// returns the matching entry (with reason) or null
export function isOnDoNotRent(license) {
  const n = normLic(license);
  if (!n) return null;
  return getArr(DNR_KEY).find((e) => normLic(e.license) === n) || null;
}
export function addDoNotRent({ license, name, reason }) {
  const list = getArr(DNR_KEY);
  const n = normLic(license);
  if (!n) return list;
  const existing = list.find((e) => normLic(e.license) === n);
  if (existing) { existing.name = name || existing.name; existing.reason = reason || existing.reason; existing.at = new Date().toISOString(); }
  else list.push({ license: String(license).trim(), name: name || '', reason: reason || '', at: new Date().toISOString() });
  save(DNR_KEY, list); return list;
}
export function removeDoNotRent(license) {
  const n = normLic(license);
  save(DNR_KEY, getArr(DNR_KEY).filter((e) => normLic(e.license) !== n));
  return getDoNotRent();
}

// total contracted days including extensions
export const totalDays = (r) => (Number(r.days) || 0) + (r.extensions || []).reduce((s, e) => s + (Number(e.days) || 0), 0);

// base charge = each period's days × that period's daily price
export function baseCharge(r) {
  let c = (Number(r.days) || 0) * (Number(r.dailyPrice) || 0);
  (r.extensions || []).forEach((e) => { c += (Number(e.days) || 0) * (Number(e.dailyPrice != null ? e.dailyPrice : r.dailyPrice) || 0); });
  return c;
}

// Live math for a rental. milesOverride (from GPS trip distance) is used when
// provided — the most reliable "miles driven since the rental started," since it
// works even when the OBD odometer reading is static/stale. Otherwise it falls
// back to a stored milesDriven, then to the odometer delta.
export function rentalMath(r, currentOdoMi, milesOverride) {
  const tDays = totalDays(r);
  const start = new Date(r.startAt).getTime();
  const dueAt = start + tDays * DAY_MS;
  const now = Date.now();
  const endRef = r.status === 'closed' && r.closedAt ? new Date(r.closedAt).getTime() : now;
  const msLeft = dueAt - now;
  const daysElapsed = Math.max(0, (endRef - start) / DAY_MS);
  const allowedMiles = tDays * (Number(r.includedMilesPerDay) || 0);
  const endOdo = r.endOdoMi != null ? r.endOdoMi : currentOdoMi;
  const odoMiles = (endOdo != null && r.startOdoMi != null) ? Math.max(0, endOdo - r.startOdoMi) : null;
  const milesDriven = milesOverride != null ? milesOverride : (r.milesDriven != null ? r.milesDriven : odoMiles);
  const overageMiles = milesDriven != null ? Math.max(0, milesDriven - allowedMiles) : 0;
  const overageCharge = overageMiles * (Number(r.overageRate) || 0);
  const base = baseCharge(r);
  // tolls: count of detected toll roads × the (editable) per-toll rate
  const tollCount = (r.tolls || []).length;
  const tollRate = r.tollRate != null ? Number(r.tollRate) : 0.5;
  const tollCharge = tollCount * tollRate;
  const total = base + overageCharge + tollCharge;
  // money: how much of the prepayment is used up, credit left, and balance owed
  const paid = Number(r.paid) || 0;
  const consumed = Math.min(total, paid);
  const prepaidLeft = Math.max(0, paid - total);
  const balanceDue = Math.max(0, total - paid);
  return {
    tDays, dueAt, msLeft,
    overdue: r.status === 'active' && msLeft < 0,
    hoursLeft: Math.floor(msLeft / (60 * 60 * 1000)),
    daysLeft: Math.floor(msLeft / DAY_MS),
    daysElapsed,
    allowedMiles, milesDriven, overageMiles, overageCharge,
    overMiles: milesDriven != null && milesDriven > allowedMiles,
    tollCount, tollRate, tollCharge,
    base, total,
    paid, consumed, prepaidLeft, balanceDue,
  };
}

// ---- reporting / revenue ----
// rentals overlapping a [from,to) window, with computed money + miles
export function rentalsInRange(deviceId, from, to, actor) {
  return getRentals(deviceId, actor).filter((r) => {
    if (r.status === 'draft' || !r.startAt) return false; // drafts aren't live rentals
    const s = new Date(r.startAt).getTime();
    const e = (r.status === 'closed' && r.closedAt ? new Date(r.closedAt).getTime() : Date.now());
    return s < to.getTime() && e >= from.getTime();
  });
}
export function summarizeRevenue(rentals) {
  return rentals.reduce((acc, r) => {
    const m = rentalMath(r, r.endOdoMi);
    acc.revenue += m.total; acc.days += m.tDays; acc.miles += m.milesDriven || 0; acc.count += 1;
    return acc;
  }, { revenue: 0, days: 0, miles: 0, count: 0 });
}

// ---- end-of-month revenue alert (fires once per car per month) ----
const MONTH_KEY = 'dd-rental-month-reported';
export function pendingMonthlyReport(deviceId) {
  const now = new Date();
  const prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevEnd = new Date(now.getFullYear(), now.getMonth(), 1); // = this month start
  const key = `${deviceId}:${prevStart.getFullYear()}-${prevStart.getMonth() + 1}`;
  if (getJSON(MONTH_KEY)[key]) return null;             // already reported
  const list = rentalsInRange(deviceId, prevStart, prevEnd);
  if (!list.length) return null;                         // nothing rented last month
  return { key, month: prevStart, sum: summarizeRevenue(list) };
}
export function markMonthlyReported(key) { const d = getJSON(MONTH_KEY); d[key] = true; save(MONTH_KEY, d); }

// ---- optional end-of-day miles report (toggle per vehicle) ----
// Keeps a daily odometer snapshot; when a new day rolls over it returns the
// miles driven since the last snapshot (so you get "yesterday: N miles").
const SNAP_KEY = 'dd-rental-daysnap';
export function pendingDailyReport(deviceId, currentOdoMi) {
  if (currentOdoMi == null) return null;
  const snaps = getJSON(SNAP_KEY);
  const today = new Date().toISOString().slice(0, 10);
  const s = snaps[deviceId];
  if (!s) { snaps[deviceId] = { day: today, odo: currentOdoMi }; save(SNAP_KEY, snaps); return null; }
  if (s.day === today) return null;
  const miles = Math.max(0, currentOdoMi - s.odo);
  snaps[deviceId] = { day: today, odo: currentOdoMi }; save(SNAP_KEY, snaps);
  return { miles, day: s.day };
}
